/**
 * Local geo verification for offline mode.
 * Decrypts encryptedAnswerData and verifies coordinates (Haversine) or FIAS IDs.
 * Used only in fullOffline mode — online mode always uses the server.
 */

import { decrypt } from '../crypto/crypto-utils';

const R = 6371000; // Earth radius in meters

/** Haversine distance between two points in meters */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface LocalVerifyResult {
  correct: boolean;
  message: string;
  // Decoded target coordinates — present for COORDINATES mode (we know them
  // after decryption), absent for ADDRESS mode (offline payload only carries
  // FIAS objectIds, not lat/lon).
  targetLat?: number;
  targetLon?: number;
}

/**
 * Verify geo answer locally using decrypted answer data.
 * encryptedAnswerData format (after decrypt):
 *   COORDINATES: "lat,lon"
 *   ADDRESS: "cityObjectId|streetObjectId|buildingObjectId"
 */
export async function verifyGeoLocal(
  encryptedAnswerData: string,
  key: Uint8Array,
  answerType: string,
  geofenceRadiusM: number,
  input: {
    lat?: number;
    lon?: number;
    cityObjectId?: number;
    streetObjectId?: number;
    buildingObjectId?: number;
  }
): Promise<LocalVerifyResult> {
  let answerData: string;
  try {
    answerData = await decrypt(encryptedAnswerData, key);
  } catch {
    return { correct: false, message: 'Ошибка дешифровки данных точки' };
  }

  if (answerType === 'COORDINATES') {
    const [targetLat, targetLon] = answerData.split(',').map(Number);
    if (!input.lat || !input.lon || isNaN(targetLat) || isNaN(targetLon)) {
      return { correct: false, message: 'Координаты не указаны' };
    }
    const distance = haversineDistance(input.lat, input.lon, targetLat, targetLon);
    if (distance <= geofenceRadiusM) {
      return { correct: true, message: `Верно! Расстояние: ${Math.round(distance)} м`, targetLat, targetLon };
    }
    return { correct: false, message: `Неверно. Расстояние: ${Math.round(distance)} м (допуск: ${geofenceRadiusM} м)` };
  }

  if (answerType === 'ADDRESS') {
    const [targetCity, targetStreet, targetBuilding] = answerData.split('|');
    if (!input.cityObjectId || !input.streetObjectId || !input.buildingObjectId) {
      return { correct: false, message: 'Выберите адрес из подсказок' };
    }
    const match = String(input.cityObjectId) === targetCity &&
                  String(input.streetObjectId) === targetStreet &&
                  String(input.buildingObjectId) === targetBuilding;
    return match
      ? { correct: true, message: 'Верно! Адрес совпадает.' }
      : { correct: false, message: 'Неверный адрес.' };
  }

  return { correct: false, message: 'Неизвестный тип ответа' };
}
